'use client'
import { Assessments } from '@crystal-eyes/types'
import SectionCard from '../../components/SectionCard'
import { Icons } from '@crystal-eyes/components/elements/Icon/Icon'
import styles from './Enneagram.module.scss'
import assessmentStyles from './Assessments.module.scss'
import Link from 'next/link'
import useIsOwnProfile from '@crystal-eyes/hooks/profiles/context/useIsOwnProfile'
import useTakingAssessment from '../../hooks/useTakingAssessment'
import useKnowYourType from '../../hooks/useKnowYourType'

interface Identity {
  id: string
}

export default function Enneagram({
  identity,
  assessments,
}: {
  identity: Identity
  assessments?: Assessments
}) {
  const { data: isOwnProfile } = useIsOwnProfile({ identity })
  const {
    assessmentState: { setTaking: setTakingAssessment },
  } = useTakingAssessment()
  const {
    entryState: { setKnowType },
  } = useKnowYourType()

  const enneagram = assessments?.enneagram

  if (!isOwnProfile && !enneagram) return null

  return (
    <SectionCard
      cardId="enneagram"
      icon={Icons.Enneagram}
      title="Enneagram"
      showPrint={true}
      className={`${assessmentStyles.assessment} ${styles.enneagram}`}
    >
      {!enneagram && (
        <div className="take-assessment-cta">
          <div className="visualization">
            <Visualization />
          </div>

          <div className="cta">
            <h4>Find out your Enneagram Type</h4>

            <p>
              Answer a few additional questions to learn more about your
              personality.
            </p>

            <button
              className="CE action-button"
              onClick={evt => {
                evt.preventDefault()
                setTakingAssessment('enneagram')
              }}
            >
              Start Test
            </button>
            <br />

            <Link
              onClick={evt => {
                evt.preventDefault()
                setKnowType('enneagram')
              }}
              href="#"
              className="already-know-link"
            >
              Already know your type?
            </Link>
          </div>
        </div>
      )}

      {enneagram && (
        <div className="assessment-visualization-results">
          <div className="visualization">
            <Visualization selected={enneagram.number} />
          </div>

          <div className="overview">
            <span className="subtitle">Enneagram Type</span>
            <h4 className="CE Parented disc-color">
              {enneagram.number}: {enneagram.name}
            </h4>

            <p>{enneagram.description}</p>

            {isOwnProfile && (
              <div className="retake-cta">
                <Link
                  href="#"
                  onClick={evt => {
                    evt.preventDefault()
                    setTakingAssessment('enneagram')
                  }}
                >
                  Re-take the assessment
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </SectionCard>
  )
}

const Visualization = ({ selected }: { selected?: number }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 588.84 624.93"
    className={`enneagram-circle ${
      selected ? `selected select-${selected}` : ''
    }`}
  >
    <g data-name="grey circle">
      <ellipse
        className="outer-circle"
        cx="297.18"
        cy="310.32"
        rx="241.67"
        ry="243.44"
      />

      <polygon
        className="lines"
        points="58.77 267.79 214.38 537.31 141.57 124.38 535.58 267.79 384.46 537.31 452.79 124.38 58.77 267.79"
      />

      <polygon
        className="lines"
        points="87.53 430.86 297.18 67.74 506.83 430.86 297.18 430.86 87.53 430.86"
      />
      <path
        className={`number CE Parented fill-disc ${
          selected === 1 ? 'selected' : 'unselected'
        } type-1`}
        d="M514.66,130.45v4.08H495v-4.08h7.5V107.9c0-.8,0-1.62.07-2.45l-5.78,4.88a1.76,1.76,0,0,1-.76.38,1.61,1.61,0,0,1-.72,0,1.8,1.8,0,0,1-.6-.24,1.69,1.69,0,0,1-.42-.36l-1.67-2.35,10.85-9.33h4.38v32Z"
      />
      <path
        className={`number CE Parented fill-disc ${
          selected === 2 ? 'selected' : 'unselected'
        } type-2`}
        d="M584.52,279.81a1.59,1.59,0,0,1,1.15.43,1.49,1.49,0,0,1,.42,1.1V284h-23.9v-1.53a2.67,2.67,0,0,1,.2-1,2.89,2.89,0,0,1,.63-.93l11.47-11.52c1-1,1.82-1.9,2.6-2.79a18.88,18.88,0,0,0,2-2.69,12.94,12.94,0,0,0,1.28-2.73,9.62,9.62,0,0,0,.45-3,7.12,7.12,0,0,0-.5-2.76,5.34,5.34,0,0,0-3.4-3.12,8,8,0,0,0-2.53-.39,7.53,7.53,0,0,0-2.52.4,6.7,6.7,0,0,0-2,1.11,6.52,6.52,0,0,0-1.49,1.69,6.86,6.86,0,0,0-.85,2.15,2,2,0,0,1-.81,1.16,2.29,2.29,0,0,1-1.44.16l-2.32-.4a13,13,0,0,1,1.36-4.33,10.66,10.66,0,0,1,2.55-3.17,10.91,10.91,0,0,1,3.51-1.93,13.81,13.81,0,0,1,8.53,0,9.87,9.87,0,0,1,3.42,2,9,9,0,0,1,2.28,3.14,10.19,10.19,0,0,1,.82,4.2,11.15,11.15,0,0,1-.6,3.71,14.8,14.8,0,0,1-1.62,3.26,26.11,26.11,0,0,1-2.36,3c-.9,1-1.84,2-2.84,3l-9.45,9.65a17.27,17.27,0,0,1,2-.43,12.51,12.51,0,0,1,2-.17Z"
      />
      <path
        className={`number CE Parented fill-disc ${
          selected === 3 ? 'selected' : 'unselected'
        } type-3`}
        d="M539.55,420.37A13.55,13.55,0,0,1,540.9,416a10.56,10.56,0,0,1,2.54-3.16,11,11,0,0,1,3.52-1.94,13.58,13.58,0,0,1,4.29-.66,13.12,13.12,0,0,1,4.2.65,9.75,9.75,0,0,1,3.32,1.85,8.51,8.51,0,0,1,2.16,2.9,9.09,9.09,0,0,1,.77,3.78,9.77,9.77,0,0,1-.43,3,7.28,7.28,0,0,1-1.25,2.34,7.7,7.7,0,0,1-2,1.7,11.39,11.39,0,0,1-2.57,1.15,9.54,9.54,0,0,1,5.26,3.09,8.4,8.4,0,0,1,1.76,5.46,10.33,10.33,0,0,1-.93,4.45A9.92,9.92,0,0,1,559,444a11.26,11.26,0,0,1-3.79,2.13,14.27,14.27,0,0,1-4.63.74,14.64,14.64,0,0,1-4.88-.72,10,10,0,0,1-3.42-2,10.3,10.3,0,0,1-2.3-3,22.51,22.51,0,0,1-1.53-3.71l1.93-.8a2.35,2.35,0,0,1,1.46-.18,1.44,1.44,0,0,1,1,.83c.21.46.49,1,.81,1.67A8,8,0,0,0,545,441a7.19,7.19,0,0,0,2.16,1.52,7.74,7.74,0,0,0,3.34.63,8.22,8.22,0,0,0,3.29-.62,7.09,7.09,0,0,0,2.35-1.58,6.8,6.8,0,0,0,1.41-2.18,6.53,6.53,0,0,0,.47-2.37,8.84,8.84,0,0,0-.37-2.65,4.51,4.51,0,0,0-1.4-2.08,7.35,7.35,0,0,0-2.82-1.37,17.13,17.13,0,0,0-4.58-.5v-3.23a14.58,14.58,0,0,0,3.91-.5,7.36,7.36,0,0,0,2.63-1.3,4.94,4.94,0,0,0,1.47-2,6.77,6.77,0,0,0,.46-2.55,6.93,6.93,0,0,0-.48-2.7,5.3,5.3,0,0,0-1.34-1.9,5.67,5.67,0,0,0-2-1.13,8.21,8.21,0,0,0-2.52-.37,7.59,7.59,0,0,0-2.51.4,6.43,6.43,0,0,0-2,1.11A6.52,6.52,0,0,0,545,417.3a6.76,6.76,0,0,0-.85,2.15,2.1,2.1,0,0,1-.81,1.16,2.26,2.26,0,0,1-1.41.16Z"
      />
      <path
        className={`number CE Parented fill-disc ${
          selected === 4 ? 'selected' : 'unselected'
        } type-4`}
        d="M398,598.58v2.55a1,1,0,0,1-.24.67.9.9,0,0,1-.74.28h-4.45v9.42h-3.9v-9.42h-15.9a1.35,1.35,0,0,1-.86-.29,1.28,1.28,0,0,1-.46-.71L371,598.8l17.42-23.12h4.15v22.9Zm-9.33,0V583.8c0-.43,0-.91.05-1.42s.09-1,.15-1.58l-13.05,17.78Z"
      />
      <path
        className={`number CE Parented fill-disc ${
          selected === 5 ? 'selected' : 'unselected'
        } type-5`}
        d="M206.72,589.2a24.8,24.8,0,0,1,5.15-.6,13.73,13.73,0,0,1,4.95.83,10.07,10.07,0,0,1,3.59,2.27,9.51,9.51,0,0,1,2.17,3.43,12,12,0,0,1,.74,4.3,12.89,12.89,0,0,1-1,5.15,11.63,11.63,0,0,1-2.72,3.94,12,12,0,0,1-4.09,2.51,14.48,14.48,0,0,1-5.07.87,14.28,14.28,0,0,1-3-.31,15.88,15.88,0,0,1-2.71-.84,16.06,16.06,0,0,1-2.34-1.2,13.82,13.82,0,0,1-1.9-1.42l1.38-1.9a1.37,1.37,0,0,1,1.17-.65,2.19,2.19,0,0,1,1.13.39c.41.25.92.54,1.52.86a13,13,0,0,0,2.12.86,9.42,9.42,0,0,0,2.88.39,9,9,0,0,0,3.38-.6,7.24,7.24,0,0,0,2.57-1.71,7.52,7.52,0,0,0,1.65-2.67,9.75,9.75,0,0,0,.58-3.47,8.76,8.76,0,0,0-.49-3,6,6,0,0,0-1.47-2.3,6.79,6.79,0,0,0-2.47-1.47,10.25,10.25,0,0,0-3.45-.53,18.55,18.55,0,0,0-2.81.23,19.33,19.33,0,0,0-3,.72l-2.8-.82,2.88-16.75h17v2a2.17,2.17,0,0,1-.58,1.56,2.74,2.74,0,0,1-2,.61H208.37Z"
      />
      <path
        className={`number CE Parented fill-disc ${
          selected === 6 ? 'selected' : 'unselected'
        } type-6`}
        d="M32.25,424.1c-.32.4-.62.78-.91,1.16s-.58.74-.84,1.11A10.51,10.51,0,0,1,33.3,425a11.29,11.29,0,0,1,3.32-.47,11.59,11.59,0,0,1,4.1.71,9.77,9.77,0,0,1,3.37,2.07,10,10,0,0,1,2.28,3.35,11.42,11.42,0,0,1,.85,4.54,11.68,11.68,0,0,1-.9,4.6,11.13,11.13,0,0,1-2.51,3.7A11.64,11.64,0,0,1,39.94,446a13.42,13.42,0,0,1-5,.9,13.2,13.2,0,0,1-4.9-.87,10.36,10.36,0,0,1-3.74-2.45,10.74,10.74,0,0,1-2.37-3.85,14.4,14.4,0,0,1-.84-5.06,13.62,13.62,0,0,1,1-5A24.19,24.19,0,0,1,27.45,424l9.1-12.22a2.79,2.79,0,0,1,1-.77,3.18,3.18,0,0,1,1.41-.31h3.95Zm2.6,19.1a8.89,8.89,0,0,0,3.26-.58,7.11,7.11,0,0,0,2.5-1.6,7,7,0,0,0,1.6-2.42,8,8,0,0,0,.56-3.05,8.64,8.64,0,0,0-.55-3.17,6.57,6.57,0,0,0-1.56-2.4,6.94,6.94,0,0,0-2.41-1.51,8.73,8.73,0,0,0-3.08-.52,8.26,8.26,0,0,0-3.25.61,7.6,7.6,0,0,0-2.46,1.65,7.24,7.24,0,0,0-1.55,2.4,7.63,7.63,0,0,0-.54,2.84,9.55,9.55,0,0,0,.5,3.15,6.88,6.88,0,0,0,1.47,2.45,6.45,6.45,0,0,0,2.35,1.58A8.21,8.21,0,0,0,34.85,443.2Z"
      />
      <path
        className={`number CE Parented fill-disc ${
          selected === 7 ? 'selected' : 'unselected'
        } type-7`}
        d="M27,248.21v2a4.5,4.5,0,0,1-.19,1.4,9.46,9.46,0,0,1-.37.93L11.62,282.46a3.14,3.14,0,0,1-.9,1.12A2.39,2.39,0,0,1,9.2,284H6l15.05-29.55c.22-.42.44-.8.67-1.15s.49-.69.75-1H3.78A1,1,0,0,1,3,252a1.06,1.06,0,0,1-.33-.76v-3Z"
      />
      <path
        className={`number CE Parented fill-disc ${
          selected === 8 ? 'selected' : 'unselected'
        } type-8`}
        d="M89.63,116.82A9.81,9.81,0,0,1,94.78,120a8.76,8.76,0,0,1,1.8,5.7,9.86,9.86,0,0,1-.89,4.23,9.32,9.32,0,0,1-2.48,3.22,11.11,11.11,0,0,1-3.81,2,17,17,0,0,1-9.8,0,11.25,11.25,0,0,1-3.82-2,9.08,9.08,0,0,1-2.48-3.22,10,10,0,0,1-.87-4.23,8.76,8.76,0,0,1,1.8-5.7,9.77,9.77,0,0,1,5.15-3.18,8.12,8.12,0,0,1-5.7-8.12,9,9,0,0,1,.77-3.7,8.9,8.9,0,0,1,2.2-3,10.38,10.38,0,0,1,3.41-2,13.84,13.84,0,0,1,8.87,0,10.32,10.32,0,0,1,3.42,2,8.77,8.77,0,0,1,2.2,3,8.86,8.86,0,0,1,.78,3.7,8.55,8.55,0,0,1-1.44,5A8.67,8.67,0,0,1,89.63,116.82ZM84.5,132.35a9.58,9.58,0,0,0,3.14-.49A6.83,6.83,0,0,0,90,130.48a6,6,0,0,0,1.47-2.12,7,7,0,0,0,.52-2.74,7.39,7.39,0,0,0-.62-3.16,5.62,5.62,0,0,0-1.65-2.14,6.78,6.78,0,0,0-2.38-1.21,10.43,10.43,0,0,0-5.65,0,6.83,6.83,0,0,0-2.39,1.21,5.83,5.83,0,0,0-1.65,2.14,7.39,7.39,0,0,0-.61,3.16,7.15,7.15,0,0,0,.51,2.74A6,6,0,0,0,79,130.48a6.92,6.92,0,0,0,2.35,1.38A9.58,9.58,0,0,0,84.5,132.35Zm0-29.63a7.62,7.62,0,0,0-2.81.48,5.72,5.72,0,0,0-2,1.3,5.29,5.29,0,0,0-1.22,1.92,6.53,6.53,0,0,0-.41,2.35,7.93,7.93,0,0,0,.35,2.38,5.54,5.54,0,0,0,1.12,2,5.68,5.68,0,0,0,2,1.43,8.55,8.55,0,0,0,6,0,5.43,5.43,0,0,0,3.14-3.46,8.26,8.26,0,0,0,.35-2.38,6.53,6.53,0,0,0-.42-2.35,5.29,5.29,0,0,0-1.22-1.92,5.63,5.63,0,0,0-2-1.3A7.55,7.55,0,0,0,84.5,102.72Z"
      />
      <path
        className={`number CE Parented fill-disc ${
          selected === 9 ? 'selected' : 'unselected'
        } type-9`}
        d="M302,28.64c.37-.49.71-.94,1-1.38s.61-.86.9-1.3a10,10,0,0,1-3.1,1.68,11.57,11.57,0,0,1-3.65.57,10.92,10.92,0,0,1-3.84-.67,9.19,9.19,0,0,1-3.19-2A9.47,9.47,0,0,1,288,22.33a11.2,11.2,0,0,1-.81-4.39,10.94,10.94,0,0,1,.88-4.4A10.82,10.82,0,0,1,290.5,10a11.71,11.71,0,0,1,3.75-2.4A12.73,12.73,0,0,1,299,6.69a12.31,12.31,0,0,1,4.68.85,10.24,10.24,0,0,1,5.91,6,13.14,13.14,0,0,1,.81,4.7,13.83,13.83,0,0,1-.29,2.94,16.1,16.1,0,0,1-.82,2.73A21,21,0,0,1,308,26.58c-.52.87-1.1,1.77-1.75,2.68l-8.73,12.6a2.25,2.25,0,0,1-.92.76,3,3,0,0,1-1.38.29h-4.1Zm-10.4-11a8.69,8.69,0,0,0,.49,3,6.05,6.05,0,0,0,1.4,2.26,5.8,5.8,0,0,0,2.22,1.4,8.55,8.55,0,0,0,2.94.48,8.17,8.17,0,0,0,3.19-.59,7.16,7.16,0,0,0,2.34-1.56,6.85,6.85,0,0,0,1.45-2.25,7.29,7.29,0,0,0,.5-2.65,8.19,8.19,0,0,0-.54-3,6.72,6.72,0,0,0-1.49-2.31,6.38,6.38,0,0,0-2.26-1.46,7.74,7.74,0,0,0-2.89-.52,8,8,0,0,0-3,.54,6.9,6.9,0,0,0-2.32,1.49,6.43,6.43,0,0,0-1.5,2.27A7.83,7.83,0,0,0,291.62,17.61Z"
      />

      <circle
        className={`CE Parented stroke-disc fill-disc dot ${
          selected === 1 ? 'selected' : 'unselected'
        } type-1`}
        cx="453.18"
        cy="124.38"
        r="12.63"
      />
      <circle
        className={`CE Parented stroke-disc fill-disc dot ${
          selected === 2 ? 'selected' : 'unselected'
        } type-2`}
        cx="533.85"
        cy="267.41"
        r="12.63"
      />
      <circle
        className={`CE Parented stroke-disc fill-disc dot ${
          selected === 3 ? 'selected' : 'unselected'
        } type-3`}
        cx="506.83"
        cy="430.86"
        r="12.63"
      />
      <circle
        className={`CE Parented stroke-disc fill-disc dot ${
          selected === 4 ? 'selected' : 'unselected'
        } type-4`}
        cx="384.46"
        cy="537.31"
        r="12.63"
      />
      <circle
        className={`CE Parented stroke-disc fill-disc dot ${
          selected === 5 ? 'selected' : 'unselected'
        } type-5`}
        cx="215.67"
        cy="537.31"
        r="12.63"
      />
      <circle
        className={`CE Parented stroke-disc fill-disc dot ${
          selected === 6 ? 'selected' : 'unselected'
        } type-6`}
        cx="87.53"
        cy="430.86"
        r="12.63"
      />
      <circle
        className={`CE Parented stroke-disc fill-disc dot ${
          selected === 7 ? 'selected' : 'unselected'
        } type-7`}
        cx="58.78"
        cy="267.41"
        r="12.63"
      />
      <circle
        className={`CE Parented stroke-disc fill-disc dot ${
          selected === 8 ? 'selected' : 'unselected'
        } type-8`}
        cx="142.51"
        cy="124.38"
        r="12.63"
      />

      <circle
        className={`CE Parented stroke-disc fill-disc dot ${
          selected === 9 ? 'selected' : 'unselected'
        } type-9`}
        cx="297.18"
        cy="67.74"
        r="12.63"
      />

      {selected && (
        <text
          x="50%"
          y="50%"
          textAnchor="middle"
          className="center-type number CE Parented stroke-disc fill-disc"
        >
          {selected}
        </text>
      )}
    </g>
  </svg>
)
